import React, { useState, useEffect } from "react";
import { Formik, Form, Field } from "formik";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "../../HOC/Axios/Axios";
import ContextApi from "../../HOC/ContextApi/ContextApi";
import { IoClose, IoCloudUploadOutline } from "react-icons/io5";
import image from "../../Resources/Images/dummyImage.svg";
import Loader from "../../components/pageComponents/Spinner/loader";
import ScrollOff from "../../components/pageComponents/WindowScrollOff/ScrollOff";
import {
  MdDashboard,
  MdTrendingUp,
  MdPeople,
  MdAccountBalance,
  MdArrowUpward,
  MdEdit,
  MdSave,
} from "react-icons/md";
import { BiCoin } from "react-icons/bi";

// ─── Stat Card Component ───────────────────────────────────────────────────────
function StatCard({ icon: Icon, label, value, sub, gradient, iconBg }) {
  return (
    <div
      className="relative overflow-hidden rounded-2xl p-5 shadow-card border border-white/60"
      style={{ background: gradient }}
    >
      {/* Decorative blobs */}
      <div
        className="absolute -top-6 -right-6 w-24 h-24 rounded-full opacity-20"
        style={{ background: "rgba(255,255,255,0.5)" }}
      />
      <div
        className="absolute bottom-2 -right-4 w-16 h-16 rounded-full opacity-10"
        style={{ background: "rgba(255,255,255,0.7)" }}
      />

      <div className="relative z-10 flex items-start justify-between">
        <div>
          <p className="text-white/70 text-xs font-semibold uppercase tracking-widest mb-1">
            {label}
          </p>
          <h3 className="text-white text-2xl font-bold leading-tight">{value}</h3>
          {sub && (
            <span className="inline-flex items-center gap-1 mt-1.5 text-white/80 text-xs font-medium">
              <MdArrowUpward className="text-emerald-300" />
              {sub}
            </span>
          )}
        </div>
        <div
          className="flex items-center justify-center w-11 h-11 rounded-xl"
          style={{ background: iconBg }}
        >
          <Icon className="text-white text-xl" />
        </div>
      </div>
    </div>
  );
}

// ─── Main Dashboard ────────────────────────────────────────────────────────────
function DashBoard() {
  const [Images, setImages] = useState("");
  const [showSpinner, setShowSpinner] = useState(false);
  const [data, setData] = useState([]);
  const [dragActive, setDragActive] = useState(false);

  const stats = [
    {
      icon: MdPeople,
      label: "Total Users",
      value: "—",
      sub: "Active members",
      gradient: "linear-gradient(135deg, #7c3aed 0%, #a855f7 100%)",
      iconBg: "rgba(255,255,255,0.2)",
    },
    {
      icon: MdAccountBalance,
      label: "Total Deposits",
      value: "—",
      sub: "All time",
      gradient: "linear-gradient(135deg, #0f172a 0%, #1e3a5f 100%)",
      iconBg: "rgba(255,255,255,0.15)",
    },
    {
      icon: BiCoin,
      label: "Trading Volume",
      value: "—",
      sub: "All time",
      gradient: "linear-gradient(135deg, #065f46 0%, #059669 100%)",
      iconBg: "rgba(255,255,255,0.2)",
    },
    {
      icon: MdTrendingUp,
      label: "Withdrawals",
      value: "—",
      sub: "Total processed",
      gradient: "linear-gradient(135deg, #b45309 0%, #f59e0b 100%)",
      iconBg: "rgba(255,255,255,0.2)",
    },
  ];

  const formFields = [
    {
      label: "Slogan",
      type: "text",
      apikey: "sologan",
      placeholder: "Enter company slogan…",
    },
    {
      label: "Description",
      type: "text",
      apikey: "description",
      placeholder: "Enter company description…",
    },
  ];

  let loader;
  if (showSpinner) loader = <Loader />;

  const handleImageChange = (e) => setImages(e.target.files[0]);
  const handleRemoveImage = () => {
    setImages("");
    const input = document.querySelector("#image");
    if (input) input.value = null;
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(e.type === "dragenter" || e.type === "dragover");
  };
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    const file = e.dataTransfer.files?.[0];
    if (file && file.type.startsWith("image/")) setImages(file);
  };

  const getData = async () => {
    setShowSpinner(true);
    try {
      const response = await axios.get("/company/getCompanyInfo");
      if (response.status === 200 && response.data) setData(response.data);
    } catch (error) {
      toast.error(error.message, { toastId: "Error" });
    } finally {
      setShowSpinner(false);
    }
  };

  useEffect(() => {
    getData();
  }, []);

  const Submits = (values, { resetForm }) => {
    setShowSpinner(true);
    const formData = new FormData();
    formData.append("sologan", values.sologan);
    formData.append("description", values.description);
    if (Images) {
      formData.append("logo", Images);
    } else if (data?.length > 0 && data[0]?.logo) {
      formData.append("logo", data[0]?.logo);
    }
    axios
      .post(`/company/postCompanyInfo`, formData)
      .then((res) => {
        if (res.status === 201) {
          toast.success(res?.data?.message, { toastId: "Added success" });
          setImages("");
          getData();
          resetForm();
        }
        setShowSpinner(false);
      })
      .catch((err) => {
        setShowSpinner(false);
        toast.error(err?.response?.data?.message);
      });
  };

  return (
    <ContextApi>
      <ScrollOff message={showSpinner}>{showSpinner && loader}</ScrollOff>
      <ToastContainer autoClose={1500} theme="colored" />

      <div className="min-h-screen bg-mainColor px-3 tablet:px-8 py-4 tablet:py-6 space-y-4 tablet:space-y-7">

        {/* ── Welcome Banner ── */}
        <div
          className="relative overflow-hidden rounded-2xl p-4 tablet:p-8 shadow-soft"
          style={{
            background: "linear-gradient(135deg, #0f172a 0%, #1e293b 60%, #312e81 100%)",
          }}
        >
          {/* Decorative circles */}
          <div
            className="absolute -top-10 -right-10 w-48 h-48 rounded-full opacity-10"
            style={{ background: "radial-gradient(circle, #7c3aed, transparent)" }}
          />
          <div
            className="absolute bottom-0 left-1/4 w-32 h-32 rounded-full opacity-10"
            style={{ background: "radial-gradient(circle, #a855f7, transparent)" }}
          />
          <div className="relative z-10 flex flex-col tablet:flex-row tablet:items-center tablet:justify-between gap-3 tablet:gap-4">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <div className="bg-secondMainColor/20 border border-secondMainColor/30 rounded-xl p-2.5">
                  <MdDashboard className="text-violet-400 text-xl" />
                </div>
                <span className="text-violet-300 text-sm font-semibold uppercase tracking-widest">
                  Admin Console
                </span>
              </div>
              <h1 className="text-white text-xl tablet:text-3xl font-bold leading-snug">
                Welcome back, <span className="text-violet-400">Admin</span> 👋
              </h1>
              <p className="text-slate-400 text-sm mt-1">
                Manage your company info, monitor activity and configure settings.
              </p>
            </div>
            <div className="flex-shrink-0 hidden tablet:flex items-center gap-2 bg-white/5 border border-white/10 rounded-xl px-4 py-3">
              <div className="text-right">
                <div className="text-slate-400 text-xs">Last updated</div>
                <div className="text-white text-sm font-semibold">
                  {new Date().toLocaleDateString("en-US", {
                    month: "short",
                    day: "numeric",
                    year: "numeric",
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ── Stats Row ── */}
        <div className="grid grid-cols-2 tablet:grid-cols-2 laptop:grid-cols-4 gap-3 tablet:gap-4">
          {stats.map((s, i) => (
            <StatCard key={i} {...s} />
          ))}
        </div>

        {/* ── Company Info Form ── */}
        <div className="bg-white rounded-2xl shadow-card border border-slate-100 overflow-hidden">
          {/* Card Header */}
          <div className="px-6 py-5 border-b border-slate-100 flex items-center gap-3">
            <div className="bg-secondMainColor/10 rounded-xl p-2.5">
              <MdEdit className="text-secondMainColor text-lg" />
            </div>
            <div>
              <h2 className="text-slate-800 font-bold text-base leading-tight">
                Company Information
              </h2>
              <p className="text-slate-500 text-xs mt-0.5">
                Update your company branding and details
              </p>
            </div>
          </div>

          {/* Card Body */}
          <div className="p-4 tablet:p-8">
            <Formik
              initialValues={{
                sologan: data?.length > 0 ? data[0]?.sologan : "",
                description: data[0]?.description ?? "",
              }}
              onSubmit={Submits}
              enableReinitialize
            >
              {({ errors, touched }) => (
                <Form className="space-y-6">
                  {/* Text Fields Grid */}
                  <div className="grid grid-cols-1 tablet:grid-cols-2 gap-4">
                    {formFields.map((val, i) => (
                      <div key={i} className="flex flex-col gap-1.5">
                        <label
                          htmlFor={val.apikey}
                          className="text-slate-700 font-semibold text-sm capitalize"
                        >
                          {val.label}
                        </label>
                        <Field
                          id={val.apikey}
                          name={val.apikey}
                          type="text"
                          placeholder={val.placeholder}
                          className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-slate-50/60
                            text-slate-800 text-sm placeholder:text-slate-400
                            focus:outline-none focus:ring-2 focus:ring-secondMainColor/25
                            focus:border-secondMainColor focus:bg-white transition-all duration-150"
                        />
                        {errors[val.apikey] && touched[val.apikey] && (
                          <span className="text-red-500 text-xs px-1">
                            {errors[val.apikey]}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>

                  {/* Logo Upload */}
                  <div>
                    <label className="text-slate-700 font-semibold text-sm block mb-3">
                      Company Logo
                    </label>

                    <div className="grid grid-cols-1 tablet:grid-cols-2 gap-5">
                      {/* Drop Zone */}
                      <div>
                        <input
                          id="image"
                          type="file"
                          accept="image/png, image/gif, image/jpeg"
                          onChange={handleImageChange}
                          className="hidden"
                        />
                        <div
                          onDragEnter={handleDrag}
                          onDragLeave={handleDrag}
                          onDragOver={handleDrag}
                          onDrop={handleDrop}
                          className={`relative rounded-2xl overflow-hidden transition-all duration-200 border-2 border-dashed
                            ${dragActive
                              ? "border-secondMainColor bg-secondMainColor/5 scale-[1.01]"
                              : "border-slate-200 bg-slate-50/50 hover:border-secondMainColor/40"
                            }`}
                          style={{ minHeight: "220px" }}
                        >
                          {Images ? (
                            <>
                              <img
                                src={URL.createObjectURL(Images)}
                                alt="Logo preview"
                                className="w-full h-[220px] object-cover object-center"
                              />
                              <button
                                type="button"
                                onClick={handleRemoveImage}
                                className="absolute top-3 right-3 bg-red-500 hover:bg-red-600 text-white p-1.5 rounded-full shadow-lg transition-colors"
                              >
                                <IoClose className="text-base" />
                              </button>
                            </>
                          ) : data?.length > 0 && data[0]?.logo ? (
                            <>
                              <img
                                src={data[0]?.logo}
                                alt="Current logo"
                                className="w-full h-[220px] object-cover object-center"
                              />
                              <div className="absolute inset-0 bg-black/30 opacity-0 hover:opacity-100 transition-opacity flex items-center justify-center">
                                <label
                                  htmlFor="image"
                                  className="cursor-pointer text-white text-sm font-semibold flex flex-col items-center gap-2"
                                >
                                  <IoCloudUploadOutline className="text-3xl" />
                                  Click to change
                                </label>
                              </div>
                            </>
                          ) : (
                            <label
                              htmlFor="image"
                              className="flex flex-col items-center justify-center h-[220px] gap-3 cursor-pointer"
                            >
                              <div className="bg-secondMainColor/10 rounded-2xl p-4">
                                <IoCloudUploadOutline className="text-secondMainColor text-3xl" />
                              </div>
                              <div className="text-center">
                                <p className="text-slate-700 font-semibold text-sm">
                                  Drop your logo here
                                </p>
                                <p className="text-slate-400 text-xs mt-0.5">
                                  or{" "}
                                  <span className="text-secondMainColor underline underline-offset-2">
                                    browse files
                                  </span>
                                </p>
                                <p className="text-slate-300 text-xs mt-2">
                                  PNG, JPG, GIF supported
                                </p>
                              </div>
                            </label>
                          )}
                        </div>

                        {/* Change logo button */}
                        <label htmlFor="image" className="mt-3 inline-block">
                          <div
                            className="cursor-pointer flex items-center gap-2 text-sm font-semibold
                              text-secondMainColor border border-secondMainColor/30 bg-secondMainColor/5
                              hover:bg-secondMainColor hover:text-white px-4 py-2 rounded-xl transition-all duration-150"
                          >
                            <IoCloudUploadOutline />
                            Change Logo
                          </div>
                        </label>
                      </div>

                      {/* Preview Info Panel */}
                      <div className="flex flex-col justify-center gap-4">
                        <div className="bg-slate-50 border border-slate-100 rounded-2xl p-5">
                          <h3 className="text-slate-700 font-semibold text-sm mb-3">
                            Upload Guidelines
                          </h3>
                          <ul className="space-y-2 text-slate-500 text-sm">
                            {[
                              "Preferred size: 512 × 512 px",
                              "Max file size: 2 MB",
                              "Formats: PNG, JPG, GIF",
                              "Transparent background recommended",
                            ].map((tip, i) => (
                              <li key={i} className="flex items-start gap-2">
                                <span className="mt-1 w-1.5 h-1.5 rounded-full bg-secondMainColor flex-shrink-0" />
                                {tip}
                              </li>
                            ))}
                          </ul>
                        </div>

                        {/* Existing logo summary */}
                        {data?.length > 0 && (
                          <div className="bg-emerald-50 border border-emerald-100 rounded-2xl p-4 flex items-center gap-3">
                            <img
                              src={data[0]?.logo || image}
                              alt="Current"
                              className="w-12 h-12 rounded-xl object-cover border border-emerald-200"
                            />
                            <div>
                              <p className="text-emerald-700 font-semibold text-sm">
                                Current logo active
                              </p>
                              <p className="text-emerald-600 text-xs mt-0.5">
                                Upload a new one to replace it
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Submit */}
                  <div className="flex items-center gap-3 pt-2 border-t border-slate-100">
                    <button
                      id="dashboard-submit-btn"
                      type="submit"
                      className="inline-flex items-center gap-2 px-7 py-3 bg-sidebar hover:bg-secondMainColor
                        text-white font-semibold rounded-xl shadow-md hover:shadow-glow
                        transition-all duration-200 text-sm"
                    >
                      <MdSave className="text-lg" />
                      {data?.length > 0 ? "Save Changes" : "Submit"}
                    </button>
                    <span className="text-slate-400 text-xs">
                      Changes will apply immediately
                    </span>
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        </div>

      </div>
    </ContextApi>
  );
}

export default DashBoard;
